import React from "react";

type ListPosition = { page: number; searchText?: string };

const readPosition = (storageKey: string): ListPosition | undefined => {
  try {
    const stored = JSON.parse(sessionStorage.getItem(storageKey) ?? "null");
    if (!Number.isInteger(stored?.page) || stored.page < 0) return undefined;
    return { page: stored.page, searchText: typeof stored.searchText === "string" ? stored.searchText : undefined };
  } catch {
    return undefined;
  }
};

/** Keeps a list's page and search text, and returns to them when `restore` is set. */
function useRememberedListPosition(storageKey?: string, restore?: boolean) {
  const [initialPosition] = React.useState(() => (storageKey && restore ? readPosition(storageKey) : undefined));
  const positionRef = React.useRef<ListPosition>(initialPosition ?? { page: 0 });

  const save = (change: Partial<ListPosition>) => {
    if (!storageKey) return;
    positionRef.current = { ...positionRef.current, ...change };
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(positionRef.current));
    } catch {
      // Storage can be unavailable (private mode, quota); the list then simply starts from page one next time.
    }
  };

  return {
    initialPage: initialPosition?.page,
    initialSearchText: initialPosition?.searchText,
    savePage: (page: number) => save({ page }),
    saveSearchText: (searchText?: string) => save({ searchText }),
  };
}

export default useRememberedListPosition;
